import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Package, Truck, Clock, Users, Calculator, 
  Phone, Mail, MapPin, Shield, Award, Star, Wrench,
  Building2, Home, ChevronRight, ExternalLink, Sparkles,
  CheckCircle, MessageSquare, HelpCircle, Play, ChevronDown, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/layout/Header';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { organizationSchema } from '../components/schemas';
import QuoteForm from '../components/QuoteForm';
import OptimizedImage from '../components/ui/OptimizedImage';
import { realProjectImages, realProductImages, supplierLogos } from '../data/mock';
import { 
  VinylFenceIcon, WoodFenceIcon, ChainLinkIcon, OrnamentalFenceIcon,
  FencePostIcon, FenceDeliveryIcon, FenceToolsIcon, PrivacyFenceIcon,
  FenceCalculatorIcon, PicketFenceIcon
} from '../components/icons/FenceIcons';
import {
  FadeIn, ScaleIn, StaggerContainer, StaggerItem, AnimatedCounter,
  HoverCard, FloatingElement, PulseElement, motion
} from '../components/animations';
import HeroVideo from '../components/HeroVideo';
import { LogoCarousel } from '../components/AnimatedLogo';

// Intersection Observer Hook for scroll animations
const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

// Product categories with real images
const productCategories = [
  { name: 'Vinyl Fencing', slug: 'vinyl', image: realProductImages.vinyl.veka2, count: '40+', description: 'Posts, rails, caps, panels', color: '#456CAC' },
  { name: 'Chain Link', slug: 'chain-link', image: realProductImages.chainLink.blackPlasticMesh, count: '25+', description: 'Posts, mesh, hardware, gates', color: '#059669' },
  { name: 'Ornamental', slug: 'ornamental', image: realProductImages.hardware.lokkLatch, count: '20+', description: 'Panels, gates, brackets', color: '#7c3aed' },
  { name: 'Wood Fencing', slug: 'wood', image: realProductImages.hardware.woodHardware, count: '15+', description: 'Boards, posts, hardware', color: '#b45309' },
  { name: 'Gates & Hardware', slug: 'gates', image: realProductImages.hardware.vinylLatch, count: '10+', description: 'Latches, hinges, closers', color: '#dc2626' },
  { name: 'Accessories', slug: 'accessories', image: realProductImages.vinyl.fortress4x4, count: '15+', description: 'Caps, brackets, collars', color: '#0891b2' },
];

const suppliers = [
  { 
    name: 'VEKA', 
    description: 'Premium Vinyl Fencing', 
    website: 'https://vekaolp.com/vekafence', 
    specialty: 'Lifetime Warranty Vinyl',
    logo: '/logos/suppliers/veka.png'
  },
  { 
    name: 'Catalyst Fence Solutions', 
    description: 'Designer Vinyl Collections', 
    website: 'https://catalystfence.com', 
    specialty: 'Woodgrain Finishes',
    logo: '/logos/suppliers/catalyst-fence-solutions.jpg'
  },
  { 
    name: 'Atlas Hardware', 
    description: 'Premium Gate Hardware', 
    website: 'https://atlashardware.ca', 
    specialty: 'Commercial Grade',
    logo: '/logos/suppliers/atlas-hardware.png'
  },
  { 
    name: 'Rona', 
    description: 'Pressure Treated Lumber', 
    website: 'https://www.rona.ca', 
    specialty: 'MicroPro Sienna',
    logo: '/logos/suppliers/rona.jpg'
  },
  { 
    name: 'Direct Fencing Supply', 
    description: 'Ornamental Iron & Aluminum', 
    website: 'https://directfencingsupply.ca', 
    specialty: 'Made in Canada',
    logo: '/logos/suppliers/dfs.png'
  },
  { 
    name: 'Malden Metals', 
    description: 'Steel & Metal Fabrication', 
    website: 'https://maldenmetals.com', 
    specialty: 'Custom Metalwork',
    logo: '/logos/suppliers/malden-metals.png'
  },
];

const trustBadges = [
  { icon: Clock, text: '10+ Years Local', subtext: 'Serving Central Alberta' },
  { icon: FencePostIcon, text: '250+ Products', subtext: 'In Stock & Ready' },
  { icon: FenceDeliveryIcon, text: 'Local Delivery', subtext: 'Central Alberta' },
  { icon: FenceToolsIcon, text: 'Custom Fabrication', subtext: 'CNC Routing' },
];

// Certifications and trust badges
const certifications = [
  {
    name: 'WCB Alberta',
    description: 'Workers\' Compensation Board Certified',
    logo: '/logos/suppliers/wcb-alberta.jpg',
    website: 'https://www.wcb.ab.ca'
  },
  {
    name: 'VEKA DualEdge',
    description: 'Eco Layer Extrusion Technology',
    logo: '/logos/suppliers/veka-dualedge.png',
    website: 'https://vekaolp.com'
  },
  {
    name: 'VEKA Recycling',
    description: 'Sustainable Outdoor Living',
    logo: '/logos/suppliers/veka-recycle.png',
    website: 'https://vekaolp.com'
  }
];

// Contractor testimonials
const testimonials = [
  {
    name: 'Mike Thompson',
    company: 'Thompson Fencing Ltd',
    location: 'Red Deer, AB',
    quote: 'CAF has been our go-to supplier for 3 years. They always have what we need in stock, and their delivery is faster than the big box stores.',
    rating: 5,
    projectType: 'Vinyl Privacy',
  },
  {
    name: 'Sarah Chen',
    company: 'Clearview Properties',
    location: 'Lacombe, AB',
    quote: 'As a property manager, I need reliable suppliers. CAF understands commercial timelines and their pricing is competitive with our Edmonton vendors.',
    rating: 5,
    projectType: 'Chain Link',
  },
  {
    name: 'Dave Morrison',
    company: 'Morrison Construction',
    location: 'Sylvan Lake, AB',
    quote: 'The material calculator on their site is actually accurate! Saved me from over-ordering on a 400ft vinyl job. Great technical support too.',
    rating: 5,
    projectType: 'Vinyl',
  },
  {
    name: 'Jennifer Walsh',
    company: 'Homeowner',
    location: 'Springbrook, AB',
    quote: 'I was nervous about DIY but their team walked me through everything. Even recommended a local installer when I decided the project was too big.',
    rating: 5,
    projectType: 'Wood',
  },
  {
    name: 'Carlos Reyes',
    company: 'Reyes Landscaping',
    location: 'Ponoka, AB',
    quote: 'The trade account pricing makes a real difference on margins. Plus they hold materials for me until the site is ready - no storage headaches.',
    rating: 5,
    projectType: 'Ornamental',
  },
];

// Featured products linking to online store
const featuredProducts = [
  { name: 'VEKA 6\' Privacy Panel', price: 'From $89', inStock: true, category: 'Vinyl' },
  { name: 'Routed 5x5 Vinyl Post', price: 'From $45', inStock: true, category: 'Vinyl' },
  { name: '6\' Galvanized Chain Link', price: 'From $12/ft', inStock: true, category: 'Chain Link' },
  { name: 'Lokk-Latch Pro SL', price: '$89', inStock: true, category: 'Hardware' },
  { name: 'Ameristar Montage Panel', price: 'Call for Price', inStock: true, category: 'Ornamental' },
  { name: 'PT 4x4x8 Post', price: 'From $18', inStock: true, category: 'Wood' },
];

const customerTypes = [
  { 
    icon: FenceToolsIcon, 
    title: 'Contractors & Builders',
    description: 'Volume pricing, quick reorder, dedicated support',
    cta: 'Open Trade Account',
    link: '/contractors'
  },
  { 
    icon: PicketFenceIcon, 
    title: 'DIY Homeowners',
    description: 'Expert advice, material calculators, how-to guides',
    cta: 'Calculate Materials',
    link: '/calculator'
  },
  { 
    icon: Building2, 
    title: 'Property Managers',
    description: 'Bulk orders, scheduled delivery, account management',
    cta: 'Request Quote',
    link: '/quote'
  },
];

const STORE_URL = 'https://mccsys.ca/store/central-alberta-fencing-inc-la24fw';
const TRADE_ACCOUNT_URL = 'https://mccsys.ca/store/cafinc/trade-account';

const LandingPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  
  // Refs for scroll animations
  const [categoriesRef, categoriesInView] = useInView();
  const [customerTypesRef, customerTypesInView] = useInView();
  const [suppliersRef, suppliersInView] = useInView();
  const [comparisonRef, comparisonInView] = useInView();
  const [faqRef, faqInView] = useInView();

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  const colors = {
    primary: '#456CAC',
    primaryDark: '#3A6178',
    primaryLight: '#6BA3C7',
    accent: '#059669',
    accentDark: '#047857',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    bg: isDark ? '#0a0a0a' : '#fafafa',
    bgAlt: isDark ? '#111111' : '#ffffff',
    surface: isDark ? '#18181b' : '#ffffff',
    surfaceAlt: isDark ? '#1f1f23' : '#f8fafc',
    border: isDark ? '#27272a' : '#e4e4e7',
    borderLight: isDark ? '#333338' : '#f1f5f9',
  };

  // FAQ data for schema
  const landingFaqs = [
    {
      question: 'Do you offer installation services?',
      answer: 'We focus on supplying materials, but we work with a network of trusted local installers. Visit our Installation Partners page to find a qualified contractor in your area.'
    },
    {
      question: 'What areas do you deliver to?',
      answer: 'We deliver throughout Central Alberta within an 80km radius of Red Deer, including Lacombe, Sylvan Lake, Innisfail, Ponoka, Stettler, and more. Same-day delivery often available!'
    },
    {
      question: 'Do you offer contractor pricing?',
      answer: 'Yes! We offer volume discounts and trade accounts for licensed contractors and property managers. Apply online or visit our store with your business credentials.'
    },
    {
      question: 'Can you custom cut materials?',
      answer: 'Absolutely. We offer on-site cutting and custom fabrication for vinyl, chain link, and ornamental fencing. Just let us know your exact measurements.'
    },
    {
      question: 'What warranty do your products have?',
      answer: 'Most of our vinyl fencing comes with a Lifetime Limited Warranty from manufacturers like VEKA. Chain link and ornamental products vary - ask us for specific warranty details.'
    },
    {
      question: 'Do I need a permit to build a fence?',
      answer: 'It depends on your municipality. Check our Bylaws & Resources section for fencing regulations in your town or city within Central Alberta.'
    },
  ];

  return (
    <div className="min-h-screen font-body" style={{ backgroundColor: colors.bg, color: colors.text }}>
      <SEO 
        title="Fence Supply Experts | Support Local - Central Alberta"
        description="Central Alberta's trusted fence material supplier. 105+ products in stock - vinyl, wood, chain link, ornamental. Authorized dealer for VEKA, Catalyst Newbury, Rona, Direct Fencing Supply & Ameristar. Local delivery, expert advice, contractor accounts."
        canonical="/"
        schema={organizationSchema}
        keywords="fence materials, vinyl fencing, chain link fence, wood fence, Red Deer fencing, Alberta fence supplier, VEKA dealer, fence posts, fence panels"
        faqs={landingFaqs}
      />
      
      {/* Announcement Banner */}
      <div 
        className="fixed top-0 left-0 right-0 z-[60] py-2.5 px-4 text-center overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }}
      >
        <div className="flex items-center justify-center gap-2 text-white relative">
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span className="text-sm font-medium">
            <span className="hidden sm:inline">Support Local, Support Your Neighbors — </span>
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-white/90 transition-colors">
              Shop 250+ Products Online
            </a>
          </span>
          <ExternalLink className="w-3 h-3" />
        </div>
      </div>

      {/* Spacer for banner */}
      <div className="h-10" />
      
      <Header />

      {/* ===== HERO SECTION ===== */}
      <section className="relative pt-32 pb-24 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center hero-mesh">
        {/* Background Image with Parallax Effect */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000"
          style={{ 
            backgroundImage: `url(${realProjectImages.vinyl.highlandUniversalTop})`,
            transform: heroLoaded ? 'scale(1)' : 'scale(1.1)',
          }}
        />
        
        {/* Enhanced Gradient Overlay */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: isDark 
              ? `linear-gradient(135deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.85) 40%, rgba(10,10,10,0.6) 70%, rgba(10,10,10,0.4) 100%)`
              : `linear-gradient(135deg, rgba(250,250,250,0.98) 0%, rgba(250,250,250,0.92) 40%, rgba(250,250,250,0.7) 70%, rgba(250,250,250,0.5) 100%)`
          }}
        />

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full opacity-20 blur-3xl animate-float" style={{ background: colors.primary }} />
        <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full opacity-10 blur-3xl animate-float" style={{ background: colors.accent, animationDelay: '2s' }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left side - Content */}
            <div className={`lg:col-span-7 text-left transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* Badge */}
              <div 
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold mb-8 badge-shine transition-all duration-700 delay-100 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ 
                  backgroundColor: isDark ? `${colors.primary}25` : `${colors.primary}15`, 
                  color: colors.primary,
                  border: `1px solid ${colors.primary}30`
                }}
              >
                <MapPin className="w-4 h-4" />
                <span>Proudly Serving Central Alberta Since 2009</span>
              </div>
              
              {/* Main Headline */}
              <h1 className={`font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 leading-[1.1] transition-all duration-700 delay-200 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                Central Alberta&apos;s{' '}
                <span className="relative inline-block">
                  <span className="gradient-text">Fence Supply</span>
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                    <path d="M2 8 Q 100 2, 198 8" stroke={colors.primary} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.4"/>
                  </svg>
                </span>{' '}
                <br className="hidden sm:block" />
                Experts
              </h1>
              
              <p className={`text-xl sm:text-2xl lg:text-3xl mb-4 font-semibold transition-all duration-700 delay-300 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ color: colors.text }}>
                Support Local. Support Your Neighbors.
              </p>
              
              <p className={`text-lg lg:text-xl mb-10 max-w-xl leading-relaxed transition-all duration-700 delay-400 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ color: colors.textMuted }}>
                250+ fence products in stock. VEKA & Catalyst authorized dealer. 
                Expert advice, custom fabrication, and delivery across Central Alberta.
              </p>
              
              {/* Primary CTAs */}
              <div className={`flex flex-wrap gap-4 mb-12 transition-all duration-700 delay-500 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="btn-premium px-8 py-7 text-lg font-semibold rounded-2xl shadow-xl group"
                    style={{ backgroundColor: colors.primary, color: '#fff' }}
                    data-testid="hero-shop-btn"
                  >
                    <Package className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" />
                    Shop Products
                    <ExternalLink className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
                <Link to="/fence/calculator">
                  <Button 
                    variant="outline"
                    className="btn-premium px-8 py-7 text-lg font-semibold rounded-2xl glass-card group"
                    style={{ borderColor: colors.primary, borderWidth: '2px', color: colors.primary }}
                    data-testid="hero-calculator-btn"
                  >
                    <Calculator className="w-5 h-5 mr-2 transition-transform group-hover:rotate-12" />
                    Material Calculator
                  </Button>
                </Link>
                <a href={TRADE_ACCOUNT_URL}>
                  <Button 
                    variant="outline"
                    className="btn-premium px-8 py-7 text-lg font-semibold rounded-2xl glass-card group"
                    style={{ borderColor: colors.border, borderWidth: '2px', color: colors.text }}
                    data-testid="hero-trade-account-btn"
                  >
                    <Building2 className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" />
                    Open Trade Account
                  </Button>
                </a>
              </div>
              
              {/* Trust Badges */}
              <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-8 transition-all duration-700 delay-600 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                {trustBadges.map((badge, index) => (
                  <div 
                    key={badge.text} 
                    className="group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:scale-105 min-w-0"
                    style={{ 
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      transitionDelay: `${index * 50}ms`
                    }}
                  >
                    <div 
                      className="icon-wrapper w-10 h-10 lg:w-11 lg:h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${colors.primary}15` }}
                    >
                      <badge.icon className="w-4 h-4 lg:w-5 lg:h-5" style={{ color: colors.primary }} />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <p className="font-semibold text-xs lg:text-sm leading-tight truncate">{badge.text}</p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: colors.textMuted }}>{badge.subtext}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side - Quote Form */}
            <div className={`hidden lg:block lg:col-span-5 transition-all duration-1000 delay-300 ${heroLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div 
                className="glass-card-strong rounded-3xl p-8 relative overflow-hidden"
                style={{ 
                  backgroundColor: isDark ? 'rgba(24,24,27,0.9)' : 'rgba(255,255,255,0.95)',
                }}
              >
                {/* Decorative corner accent */}
                <div 
                  className="absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-10"
                  style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)` }}
                />
                
                <div className="flex items-center gap-4 mb-8 relative">
                  <div 
                    className="icon-wrapper w-14 h-14 rounded-2xl flex items-center justify-center animate-pulse-glow"
                    style={{ backgroundColor: `${colors.primary}15` }}
                  >
                    <MessageSquare className="w-7 h-7" style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-heading">Get a Free Quote</h3>
                    <p className="text-sm mt-1" style={{ color: colors.textMuted }}>We respond within 24 hours</p>
                  </div>
                </div>
                <QuoteForm variant="compact" />
              </div>
            </div>
          </div>
          
          {/* Scroll indicator */}
          <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 delay-1000 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-xs font-medium uppercase tracking-widest" style={{ color: colors.textMuted }}>Explore</span>
            <ChevronDown className="w-5 h-5 animate-bounce" style={{ color: colors.primary }} />
          </div>
        </div>
      </section>

      {/* ===== PRODUCT CATEGORIES SECTION ===== */}
      <section 
        ref={categoriesRef}
        className="section-spacing relative noise-overlay" 
        style={{ backgroundColor: colors.surface }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${categoriesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span 
              className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-2 rounded-full"
              style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
            >
              Shop by Category
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Everything You Need to Build
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              Professional-grade materials at competitive prices
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {productCategories.map((category, index) => (
              <a
                key={category.slug}
                href={`${STORE_URL}?category=${category.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`interactive-card group relative rounded-3xl overflow-hidden transition-all duration-500 ${categoriesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.bgAlt, 
                  border: `1px solid ${colors.border}`,
                  transitionDelay: `${index * 100}ms`
                }}
                onMouseEnter={() => setHoveredCategory(category.slug)}
                onMouseLeave={() => setHoveredCategory(null)}
                data-testid={`category-${category.slug}`}
              >
                {/* Product Image */}
                <div className="relative h-48 overflow-hidden">
                  <OptimizedImage 
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full"
                    placeholderColor={isDark ? '#27272a' : '#e2e8f0'}
                  />
                  <div 
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{ 
                      background: `linear-gradient(to top, ${isDark ? colors.bgAlt : 'rgba(255,255,255,0.9)'} 0%, transparent 60%)`,
                      opacity: hoveredCategory === category.slug ? 0.9 : 1
                    }}
                  />
                  <span 
                    className="absolute top-4 right-4 text-xs font-bold px-4 py-1.5 rounded-full backdrop-blur-md transition-transform duration-300 group-hover:scale-110"
                    style={{ 
                      backgroundColor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
                      color: colors.primary 
                    }}
                  >
                    {category.count} items
                  </span>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-[#456CAC] transition-colors">{category.name}</h3>
                  <p className="text-sm mb-5" style={{ color: colors.textMuted }}>{category.description}</p>
                  <div 
                    className="flex items-center text-sm font-semibold transition-all duration-300 group-hover:gap-3" 
                    style={{ color: category.color }}
                  >
                    <span>Shop Now</span>
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-2" />
                  </div>
                </div>
                
                {/* Hover gradient overlay */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ 
                    background: `linear-gradient(135deg, ${category.color}08 0%, transparent 50%)`
                  }}
                />
              </a>
            ))}
          </div>
          
          <div className={`text-center mt-14 transition-all duration-700 delay-500 ${categoriesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                variant="outline" 
                className="btn-premium px-8 py-4 rounded-2xl font-semibold text-base"
                style={{ borderColor: colors.primary, borderWidth: '2px', color: colors.primary }}
              >
                View All 250+ Products
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ===== CUSTOMER TYPES SECTION ===== */}
      <section 
        ref={customerTypesRef}
        className="section-spacing relative" 
        style={{ backgroundColor: colors.bg }}
      >
        {/* Subtle pattern background */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(${colors.primary} 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${customerTypesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span 
              className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-2 rounded-full"
              style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
            >
              Who We Serve
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Built for Pros & DIYers
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {customerTypes.map((type, index) => (
              <div
                key={type.title}
                className={`group rounded-3xl p-8 lg:p-10 text-center transition-all duration-500 hover:shadow-2xl ${customerTypesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.surface, 
                  border: `1px solid ${colors.border}`,
                  transitionDelay: `${index * 150}ms`
                }}
              >
                <div 
                  className="icon-wrapper w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-8 transition-all duration-300 group-hover:shadow-lg"
                  style={{ 
                    backgroundColor: `${colors.primary}12`,
                    boxShadow: `0 0 0 8px ${colors.primary}05`
                  }}
                >
                  <type.icon className="w-9 h-9 transition-transform duration-300" style={{ color: colors.primary }} />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-4">{type.title}</h3>
                <p className="text-base mb-8 leading-relaxed" style={{ color: colors.textMuted }}>{type.description}</p>
                <Link to={type.link}>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-xl py-6 font-semibold transition-all duration-300 group-hover:bg-[#456CAC] group-hover:text-white group-hover:border-[#456CAC]"
                    style={{ borderColor: colors.border }}
                  >
                    {type.cta}
                    <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SUPPLIERS SECTION ===== */}
      <section 
        ref={suppliersRef}
        className="section-spacing relative overflow-hidden" 
        style={{ backgroundColor: colors.surface }}
      >
        {/* Decorative gradient orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-5" style={{ background: colors.primary }} />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-5" style={{ background: colors.accent }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${suppliersInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span 
              className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-2 rounded-full"
              style={{ backgroundColor: `${colors.accent}10`, color: colors.accent }}
            >
              Authorized Dealer
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Premium Suppliers, Quality Guaranteed
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              We partner with industry leaders to bring you the best materials
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 lg:gap-6">
            {suppliers.map((supplier, index) => (
              <a
                key={supplier.name}
                href={supplier.website}
                target="_blank"
                rel="noopener noreferrer"
                className={`group rounded-2xl p-6 text-center transition-all duration-500 hover:shadow-xl ${suppliersInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.bg, 
                  border: `1px solid ${colors.border}`,
                  transitionDelay: `${index * 80}ms`
                }}
              >
                <div 
                  className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-4 overflow-hidden transition-all duration-300 group-hover:shadow-lg group-hover:scale-105"
                  style={{ backgroundColor: '#fff', border: `1px solid ${colors.borderLight}` }}
                >
                  <img 
                    src={supplier.logo} 
                    alt={`${supplier.name} logo`} 
                    className="w-20 h-20 object-contain"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-base font-semibold mb-1.5 transition-colors group-hover:text-[#456CAC]">{supplier.name}</h3>
                <p className="text-xs mb-3" style={{ color: colors.textMuted }}>{supplier.description}</p>
                <span 
                  className="inline-block text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-300 group-hover:scale-105"
                  style={{ backgroundColor: `${colors.accent}10`, color: colors.accent }}
                >
                  {supplier.specialty}
                </span>
                <div className="flex items-center justify-center gap-1.5 mt-4 text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0" style={{ color: colors.primary }}>
                  Visit Website <ExternalLink className="w-3 h-3" />
                </div>
              </a>
            ))}
          </div>

          {/* Certifications Row */}
          <div className="mt-16 pt-12 border-t" style={{ borderColor: colors.border }}>
            <p className="text-center text-sm font-medium mb-8" style={{ color: colors.textMuted }}>
              Certifications & Sustainability
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {certifications.map((cert, index) => (
                <a
                  key={cert.name}
                  href={cert.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center transition-all duration-300 hover:scale-105"
                  title={cert.description}
                >
                  <div 
                    className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden transition-shadow duration-300 group-hover:shadow-lg"
                    style={{ backgroundColor: '#fff', border: `1px solid ${colors.borderLight}` }}
                  >
                    <img 
                      src={cert.logo} 
                      alt={cert.name}
                      className="w-16 h-16 object-contain"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs mt-2 text-center font-medium" style={{ color: colors.textMuted }}>
                    {cert.name}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Animated Logo Carousel */}
          <div className="mt-16 pt-12 border-t" style={{ borderColor: colors.border }}>
            <p className="text-center text-sm font-medium mb-8" style={{ color: colors.textMuted }}>
              Trusted By Leading Manufacturers
            </p>
            <LogoCarousel 
              logos={suppliers.map(s => ({ name: s.name, src: s.logo }))}
              speed={40}
              grayscale={true}
              pauseOnHover={true}
            />
          </div>
        </div>
      </section>

      {/* ===== DELIVERY & PICKUP SECTION ===== */}
      <section className="section-spacing" style={{ backgroundColor: colors.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span 
                className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-2 rounded-full"
                style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
              >
                Convenient Options
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
                Pickup or Delivery Across Central Alberta
              </h2>
              <div className="space-y-6 mb-10">
                {[
                  { icon: Package, title: 'In-Store Pickup', desc: 'Order online, pickup same day. Mon-Fri 8am-4:30pm' },
                  { icon: Truck, title: 'Local Delivery', desc: 'Delivery available throughout Central Alberta. Rates vary by distance.' },
                  { icon: Wrench, title: 'Custom Fabrication', desc: 'CNC routing for custom vinyl profiles. Ask about lead times.' }
                ].map((item, index) => (
                  <div 
                    key={item.title}
                    className="group flex items-start gap-5 p-5 rounded-2xl transition-all duration-300 hover:shadow-lg"
                    style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
                  >
                    <div 
                      className="icon-wrapper w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${colors.primary}12` }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: colors.primary }} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1.5">{item.title}</h4>
                      <p className="text-sm leading-relaxed" style={{ color: colors.textMuted }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a href={`tel:4039865484`}>
                  <Button 
                    className="btn-premium rounded-xl px-6 py-5 font-semibold"
                    style={{ backgroundColor: colors.primary, color: '#fff' }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    (403) 986-5484
                  </Button>
                </a>
                <a href="mailto:orders@cafinc.ca">
                  <Button variant="outline" className="rounded-xl px-6 py-5 font-semibold" style={{ borderColor: colors.border }}>
                    <Mail className="w-4 h-4 mr-2" />
                    orders@cafinc.ca
                  </Button>
                </a>
              </div>
            </div>
            
            <div 
              className="rounded-3xl overflow-hidden h-96 shadow-2xl"
              style={{ border: `1px solid ${colors.border}` }}
            >
              <iframe
                src="https://www.google.com/maps?q=116-25+Belich+Crescent,+Red+Deer+County,+Alberta&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: isDark ? 'invert(90%) hue-rotate(180deg)' : 'none' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Store Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== INSTALLATION NOTE SECTION ===== */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: colors.surface }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div 
            className="glass-card rounded-3xl p-10 lg:p-12 relative overflow-hidden"
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
          >
            <div 
              className="absolute top-0 left-0 w-full h-1"
              style={{ background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)` }}
            />
            <h3 className="font-heading text-2xl lg:text-3xl font-semibold mb-4">
              Need Installation?
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              We work with trusted local contractors who can install your materials. 
              Buy from us, and we&apos;ll connect you with a certified installer.
            </p>
            <Link to="/fence/installation">
              <Button variant="outline" className="btn-premium rounded-xl px-8 py-5 font-semibold" style={{ borderColor: colors.border }}>
                Learn About Installation Partners
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US COMPARISON SECTION ===== */}
      <section 
        ref={comparisonRef}
        className="section-spacing relative" 
        style={{ backgroundColor: colors.bg }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${comparisonInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span 
              className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-2 rounded-full"
              style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
            >
              Why Central Alberta Fencing
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Us vs. The Big Box Stores
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {/* CAF Column */}
            <div className={`md:col-span-2 transition-all duration-700 ${comparisonInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div 
                className="rounded-3xl p-8 lg:p-10 h-full relative overflow-hidden"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primary}03 100%)`,
                  border: `2px solid ${colors.primary}25`
                }}
              >
                {/* Top accent bar */}
                <div 
                  className="absolute top-0 left-0 right-0 h-1.5"
                  style={{ background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)` }}
                />
                
                <div className="flex items-center gap-4 mb-8">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }}
                  >
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-heading">Central Alberta Fencing</h3>
                    <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Your Local Experts</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {[
                    { title: 'Expert Advice', desc: 'Staff who actually install fences and know the products inside out' },
                    { title: 'Custom Fabrication', desc: 'On-site cutting and custom sizing for your exact needs' },
                    { title: 'Local Delivery', desc: 'Fast delivery across Central Alberta, often same/next day' },
                    { title: 'Warranty Support', desc: 'Direct access to manufacturers for warranty claims' },
                    { title: 'Contractor Pricing', desc: 'Volume discounts and trade accounts for professionals' },
                    { title: 'Full Selection', desc: 'VEKA, Catalyst, Direct Fencing - all major brands in stock' },
                  ].map((item, index) => (
                    <div 
                      key={item.title} 
                      className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-500 hover:bg-white/50 dark:hover:bg-white/5 ${comparisonInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                      style={{ transitionDelay: `${index * 100 + 300}ms` }}
                    >
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${colors.primary}15` }}
                      >
                        <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">{item.title}</p>
                        <p className="text-sm leading-relaxed" style={{ color: colors.textMuted }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Big Box Column */}
            <div className={`transition-all duration-700 delay-200 ${comparisonInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div 
                className="rounded-3xl p-8 h-full"
                style={{ backgroundColor: isDark ? '#141414' : '#f1f5f9', border: `1px solid ${colors.border}` }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-slate-200 dark:bg-slate-700">
                    <Building2 className="w-8 h-8 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-heading" style={{ color: colors.textMuted }}>Big Box Stores</h3>
                    <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Generic Experience</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {[
                    'Limited product knowledge',
                    'No custom cutting available',
                    'Generic sizes only',
                    'Long delivery wait times',
                    'No contractor support',
                    'Limited brand selection',
                  ].map((item, index) => (
                    <div 
                      key={item} 
                      className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-500 ${comparisonInView ? 'opacity-100' : 'opacity-0'}`}
                      style={{ color: colors.textMuted, transitionDelay: `${index * 80 + 400}ms` }}
                    >
                      <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <X className="w-4 h-4 text-slate-400" />
                      </div>
                      <p className="text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION ===== */}
      <section 
        className="py-20 lg:py-28 relative overflow-hidden"
        style={{ backgroundColor: colors.surfaceAlt }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span 
              className="inline-block px-4 py-1.5 text-sm font-semibold rounded-full mb-4"
              style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}
            >
              What Contractors Say
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Trusted by Local Pros
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              Hear from contractors and homeowners across Central Alberta
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((testimonial, idx) => (
              <div 
                key={idx}
                className="rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ 
                  backgroundColor: colors.surface, 
                  border: `1px solid ${colors.border}` 
                }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-sm mb-4 italic leading-relaxed" style={{ color: colors.textMuted }}>
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                
                <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <div>
                    <p className="font-semibold text-sm">{testimonial.name}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>
                      {testimonial.company} • {testimonial.location}
                    </p>
                  </div>
                  <span 
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
                  >
                    {testimonial.projectType}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link
              to="/fence/case-studies"
              className="inline-flex items-center gap-2 font-medium hover:underline"
              style={{ color: colors.primary }}
            >
              View Project Case Studies
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS SECTION ===== */}
      <section 
        className="py-20 lg:py-28 relative overflow-hidden"
        style={{ backgroundColor: colors.bg }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span 
                className="inline-block px-4 py-1.5 text-sm font-semibold rounded-full mb-4"
                style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
              >
                Shop Now
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
                Featured Products
              </h2>
              <p style={{ color: colors.textMuted }}>
                Popular items in stock and ready for pickup or delivery
              </p>
            </div>
            <a
              href={STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 md:mt-0 px-6 py-3 rounded-xl font-semibold"
              style={{ backgroundColor: colors.primary, color: '#fff' }}
            >
              <Package className="w-5 h-5" />
              View All Products
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {featuredProducts.map((product, idx) => (
              <a
                key={idx}
                href={STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ 
                  backgroundColor: colors.surface, 
                  border: `1px solid ${colors.border}` 
                }}
              >
                <div 
                  className="w-full aspect-square rounded-lg mb-3 flex items-center justify-center"
                  style={{ backgroundColor: isDark ? '#27272a' : '#f1f5f9' }}
                >
                  <Package className="w-8 h-8" style={{ color: colors.textMuted }} />
                </div>
                
                <div className="flex items-center justify-between mb-1">
                  <span 
                    className="text-xs font-medium"
                    style={{ color: colors.primary }}
                  >
                    {product.category}
                  </span>
                  {product.inStock && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      In Stock
                    </span>
                  )}
                </div>
                
                <h3 className="font-medium text-sm mb-1 group-hover:text-[#456CAC] transition-colors line-clamp-2">
                  {product.name}
                </h3>
                
                <p className="text-sm font-semibold" style={{ color: colors.primary }}>
                  {product.price}
                </p>
              </a>
            ))}
          </div>
          
          <div 
            className="mt-10 p-6 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4"
            style={{ 
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(74,124,155,0.05)',
              border: `1px solid ${colors.border}`
            }}
          >
            <div className="text-center md:text-left">
              <p className="font-semibold mb-1">Check Live Inventory & Pricing</p>
              <p className="text-sm" style={{ color: colors.textMuted }}>
                Our online store shows real-time stock levels and current pricing
              </p>
            </div>
            <a
              href={STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium whitespace-nowrap"
              style={{ 
                backgroundColor: 'transparent', 
                color: colors.primary,
                border: `2px solid ${colors.primary}`
              }}
            >
              Visit Online Store
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== FAQ SECTION ===== */}
      <section 
        ref={faqRef}
        className="section-spacing" 
        style={{ backgroundColor: colors.surface }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${faqInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span 
              className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-2 rounded-full"
              style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
            >
              Frequently Asked Questions
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Common Questions
            </h2>
          </div>
          
          <div className="space-y-5">
            {[
              {
                q: 'Do you offer installation services?',
                a: 'We focus on supplying materials, but we work with a network of trusted local installers. Visit our Installation Partners page to find a qualified contractor in your area.'
              },
              {
                q: 'What areas do you deliver to?',
                a: 'We deliver throughout Central Alberta within an 80km radius of Red Deer, including Lacombe, Sylvan Lake, Innisfail, Ponoka, Stettler, and more. Same-day delivery often available!'
              },
              {
                q: 'Do you offer contractor pricing?',
                a: 'Yes! We offer volume discounts and trade accounts for licensed contractors and property managers. Apply online or visit our store with your business credentials.'
              },
              {
                q: 'Can you custom cut materials?',
                a: 'Absolutely. We offer on-site cutting and custom fabrication for vinyl, chain link, and ornamental fencing. Just let us know your exact measurements.'
              },
              {
                q: 'What warranty do your products have?',
                a: 'Most of our vinyl fencing comes with a Lifetime Limited Warranty from manufacturers like VEKA. Chain link and ornamental products vary - ask us for specific warranty details.'
              },
              {
                q: 'Do I need a permit to build a fence?',
                a: 'It depends on your municipality. Check our Bylaws & Resources section for fencing regulations in your town or city within Central Alberta.'
              },
            ].map((faq, idx) => (
              <div 
                key={idx}
                className={`group rounded-2xl p-6 lg:p-7 transition-all duration-500 hover:shadow-lg ${faqInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ 
                  backgroundColor: colors.bg, 
                  border: `1px solid ${colors.border}`,
                  transitionDelay: `${idx * 80}ms`
                }}
              >
                <div className="flex items-start gap-5">
                  <div 
                    className="icon-wrapper w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${colors.primary}12` }}
                  >
                    <HelpCircle className="w-5 h-5" style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-[#456CAC] transition-colors">{faq.q}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: colors.textMuted }}>{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA SECTION ===== */}
      <section 
        className="py-24 lg:py-32 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }}
      >
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full border border-white" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/30" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl lg:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
            Shop online or visit our store in Red Deer County
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="btn-premium px-10 py-7 text-lg font-semibold rounded-2xl bg-white hover:bg-gray-50 shadow-2xl group"
                style={{ color: colors.primary }}
              >
                <Package className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" />
                Shop Online
              </Button>
            </a>
            <Link to="/fence/calculator">
              <Button 
                className="btn-premium px-10 py-7 text-lg font-semibold rounded-2xl bg-white/15 hover:bg-white/25 text-white border-2 border-white/40 backdrop-blur-sm"
              >
                <Calculator className="w-5 h-5 mr-2" />
                Calculate Materials
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== PREMIUM FOOTER ===== */}
      <footer 
        className="py-20 lg:py-24 border-t noise-overlay relative" 
        style={{ backgroundColor: isDark ? '#0a0a0a' : colors.surfaceAlt, borderColor: colors.border }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
            <div>
              <h3 className="font-heading text-xl font-bold mb-5">Central Alberta Fencing</h3>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: colors.textMuted }}>
                Your local fence supply experts since 2009. Support local, support your neighbors.
              </p>
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5" style={{ color: colors.primary }} />
                <span className="text-sm font-medium" style={{ color: colors.textMuted }}>Licensed & Insured</span>
              </div>
              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.facebook.com/cafprojects" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  style={{ backgroundColor: `${colors.primary}12` }}
                  data-testid="social-facebook"
                >
                  <svg className="w-5 h-5" style={{ color: colors.primary }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/centralalbertafencing" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  style={{ backgroundColor: `${colors.primary}12` }}
                  data-testid="social-instagram"
                >
                  <svg className="w-5 h-5" style={{ color: colors.primary }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-semibold mb-5">Products</h3>
              <ul className="space-y-3 text-sm" style={{ color: colors.textMuted }}>
                {['Vinyl Fencing', 'Chain Link', 'Ornamental', 'Wood Fencing', 'Gates & Hardware'].map(item => (
                  <li key={item}>
                    <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="link-underline transition-colors hover:text-[#456CAC]">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-semibold mb-5">Resources</h3>
              <ul className="space-y-3 text-sm" style={{ color: colors.textMuted }}>
                <li><Link to="/fence/calculator" className="link-underline transition-colors hover:text-[#456CAC]">Material Calculator</Link></li>
                <li><Link to="/fence/contractors" className="link-underline transition-colors hover:text-[#456CAC]">Contractor Accounts</Link></li>
                <li><Link to="/fence/installation" className="link-underline transition-colors hover:text-[#456CAC]">Installation Partners</Link></li>
                <li><Link to="/fence/bylaws" className="link-underline transition-colors hover:text-[#456CAC]">Municipal Bylaws</Link></li>
                <li><Link to="/fence" className="link-underline transition-colors hover:text-[#456CAC]">Product Guides</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-semibold mb-5">Contact</h3>
              <div className="space-y-4 text-sm" style={{ color: colors.textMuted }}>
                <a 
                  href="tel:4039865484" 
                  className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:shadow-md font-semibold text-base group"
                  style={{ backgroundColor: `${colors.primary}08`, color: colors.primary }}
                >
                  <Phone className="w-5 h-5 transition-transform group-hover:scale-110" />
                  (403) 986-5484
                </a>
                <a href="mailto:orders@cafinc.ca" className="flex items-center gap-3 transition-colors hover:text-[#456CAC]">
                  <Mail className="w-4 h-4" style={{ color: colors.primary }} />
                  orders@cafinc.ca
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.primary }} />
                  <span>116-25 Belich Crescent<br />Red Deer County, AB</span>
                </div>
                <p className="pt-2 font-semibold" style={{ color: colors.text }}>Mon-Fri: 8:00 AM - 4:30 PM</p>
              </div>
            </div>
          </div>
          
          <div 
            className="mt-16 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4" 
            style={{ borderColor: colors.border }}
          >
            <p className="text-sm" style={{ color: colors.textMuted }}>
              © {new Date().getFullYear()} Central Alberta Fencing Inc. All rights reserved.
            </p>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              Part of <span className="font-semibold">CAF Group of Companies</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
